<script setup lang="ts">
  const { t, tmResolved } = useResolvedI18n()
  const localePath = useLocalePath()

  useHead({ title: () => t('faculty.title') })

  const activeTab = ref('tab-academic')

  const academicItems = computed(() => tmResolved<string[]>('faculty.detail.academicItems'))
  const industryItems = computed(() => tmResolved<string[]>('faculty.detail.industryItems'))
  const aiItems = computed(() => tmResolved<string[]>('faculty.detail.aiItems'))
  const collabItems = computed(() => tmResolved<string[]>('faculty.detail.collabItems'))
  const pathRows = computed(() =>
    tmResolved<
      Array<{
        stage: string
        role: string
        position: string
        tasks: string
      }>
    >('faculty.path.rows'),
  )
</script>

<template>
  <section
    class="page-banner"
    style="
      background-image: url('https://www.sanyau.edu.cn/_upload/article/images/f7/15/b0d6ed2f4d5e89390ed221757d8d/9174398f-0828-4e6b-8b4f-735516a9500d.jpg');
    "
  >
    <h2>{{ t('faculty.banner') }}</h2>
    <div class="breadcrumb">
      <NuxtLink :to="localePath('/')">{{ t('common.home') }}</NuxtLink>
      &gt; {{ t('faculty.breadcrumb') }}
    </div>
  </section>

  <section class="content-section">
    <div class="container">
      <div class="full-content">
        <div class="content-card">
          <h3>{{ t('faculty.staffing.title') }}</h3>
          <p>{{ t('faculty.staffing.intro') }}</p>

          <div class="mentor-grid" style="margin-top: 25px">
            <div class="mentor-card">
              <h3>{{ t('faculty.staffing.academicTitle') }}</h3>
              <div class="mentor-title">{{ t('faculty.staffing.academicRole') }}</div>
              <p>{{ t('faculty.staffing.academicDesc') }}</p>
            </div>
            <div class="mentor-card">
              <h3>{{ t('faculty.staffing.industryTitle') }}</h3>
              <div class="mentor-title">{{ t('faculty.staffing.industryRole') }}</div>
              <p>{{ t('faculty.staffing.industryDesc') }}</p>
            </div>
            <div class="mentor-card">
              <h3>{{ t('faculty.staffing.engineerTitle') }}</h3>
              <div class="mentor-title">{{ t('faculty.staffing.engineerRole') }}</div>
              <p>{{ t('faculty.staffing.engineerDesc') }}</p>
            </div>
          </div>
        </div>

        <div class="content-card">
          <h3>{{ t('faculty.detail.title') }}</h3>
          <p>{{ t('faculty.detail.intro') }}</p>

          <div class="tabs" style="margin-top: 20px">
            <button :class="['tab-btn', { active: activeTab === 'tab-academic' }]" @click="activeTab = 'tab-academic'">
              {{ t('faculty.detail.tabs.academic') }}
            </button>
            <button :class="['tab-btn', { active: activeTab === 'tab-industry' }]" @click="activeTab = 'tab-industry'">
              {{ t('faculty.detail.tabs.industry') }}
            </button>
            <button :class="['tab-btn', { active: activeTab === 'tab-ai' }]" @click="activeTab = 'tab-ai'">
              {{ t('faculty.detail.tabs.ai') }}
            </button>
            <button :class="['tab-btn', { active: activeTab === 'tab-collab' }]" @click="activeTab = 'tab-collab'">
              {{ t('faculty.detail.tabs.collab') }}
            </button>
          </div>

          <div id="tab-academic" :class="['tab-panel', { active: activeTab === 'tab-academic' }]">
            <h4 style="color: var(--theme-blue); margin-bottom: 12px">
              {{ t('faculty.detail.academicTitle') }}
            </h4>
            <ul>
              <li v-for="(item, i) in academicItems" :key="i">{{ item }}</li>
            </ul>
          </div>

          <div id="tab-industry" :class="['tab-panel', { active: activeTab === 'tab-industry' }]">
            <h4 style="color: var(--theme-blue); margin-bottom: 12px">
              {{ t('faculty.detail.industryTitle') }}
            </h4>
            <ul>
              <li v-for="(item, i) in industryItems" :key="i">{{ item }}</li>
            </ul>
          </div>

          <div id="tab-ai" :class="['tab-panel', { active: activeTab === 'tab-ai' }]">
            <h4 style="color: var(--theme-blue); margin-bottom: 12px">
              {{ t('faculty.detail.aiTitle') }}
            </h4>
            <ul>
              <li v-for="(item, i) in aiItems" :key="i">{{ item }}</li>
            </ul>
          </div>

          <div id="tab-collab" :class="['tab-panel', { active: activeTab === 'tab-collab' }]">
            <h4 style="color: var(--theme-blue); margin-bottom: 12px">
              {{ t('faculty.detail.collabTitle') }}
            </h4>
            <ul>
              <li v-for="(item, i) in collabItems" :key="i">{{ item }}</li>
            </ul>
          </div>
        </div>

        <div class="content-card">
          <h3>{{ t('faculty.path.title') }}</h3>
          <p>{{ t('faculty.path.intro') }}</p>

          <div class="table-scroll">
            <table class="data-table" style="margin-top: 20px">
              <thead>
                <tr>
                  <th style="width: 100px">{{ t('faculty.path.colStage') }}</th>
                  <th style="width: 120px">{{ t('faculty.path.colRole') }}</th>
                  <th>{{ t('faculty.path.colPosition') }}</th>
                  <th>{{ t('faculty.path.colTasks') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in pathRows" :key="i">
                  <td>
                    <strong>{{ row.stage }}</strong>
                  </td>
                  <td v-html="row.role"></td>
                  <td>{{ row.position }}</td>
                  <td>{{ row.tasks }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
